/**
 * D-4 支付 —— `/api/payments`（契约 §7 D-4，2026-09-10 网关路由已配）。
 *
 * ⚠⚠ **网关那条路由是【精确路径】`/api/payments`，不是 `/api/payments/**`。**
 *   后端刻意收紧的：同前缀下还挂着 `POST /api/payments/test`（0.01 元入口、
 *   **不校验订单归属**），精确匹配让它在公网上根本没有路由 —— 即使哪天生产误开了
 *   那个开关。⇒ **本层只许有 `create` 一个方法**；将来后端加子路径端点（例如
 *   `GET /api/payments/{paymentNo}`）时，**网关要先加一条路由**，否则那个端点
 *   在网关 404 而 payment 自己的测试全绿。别以为前端加个方法就能调通。
 *
 * ⚠ 要登录（网关「匿名禁止 `/api/payments/**`」），默认 `auth: 'required'`。
 */
import {
  createPaymentRequestSchema,
  paymentInvokeSchema,
  type PaymentChannel,
  type PaymentInvoke,
} from '@cione/contracts'
import type { HttpClient } from './http'

export interface PaymentsApi {
  /**
   * 发起一次支付，拿回拉起参数。
   *
   * ⚠⚠ **幂等**：同一订单重复调，会话还在就返回**同一个**未过期会话（`prepayId` 不变，
   *   只重签拉起参数），过期了才向渠道重新下单。
   *   ⇒ 调用方**不需要**先判断「当前有没有可用会话」—— 没有那个接口，也不需要有。
   *   ⇒ 但**别缓存返回值**：幂等的「未过期」带 10 分钟安全余量，剩余不足时会换新会话
   *     （否则会话可能在用户输密码的**中途**失效）。每次要付款就重新调。
   *
   * ⚠ 三个错误码各有各的处置，**别混成一句「支付失败」**：
   *   - `COMMON.NOT_FOUND`（404）订单不存在**或不是你的** —— 后端刻意不区分
   *     （区分了就是给攻击者一个订单号探测器），文案只能写「找不到这个订单」；
   *   - `COMMON.CONFLICT`（409）订单状态不允许支付 —— ⚠⚠ **这多半不是错误，是竞态**：
   *     下单成功但状态还没回填到 `ORDER_PENDING_PAYMENT`。契约点名要求
   *     「轮询到状态变了再给支付入口，不要把 CONFLICT 当成错误弹给用户」；
   *   - `COMMON.UPSTREAM_UNAVAILABLE`（503）渠道拒绝下单（如 appid 与商户号未绑定）。
   *     ⚠ 它 `retryable=true`，http 层会自己退避重试，到这里时已经重试过了。
   */
  create(orderNo: string, channel: PaymentChannel): Promise<PaymentInvoke>
}

export function createPaymentsApi(http: HttpClient): PaymentsApi {
  return {
    create(orderNo, channel) {
      return http.request({
        method: 'POST',
        path: '/api/payments',
        /*
          ⚠⚠ 过一遍 strict schema 再发出去。看着多余 —— 类型已经限住了 ——
          但它拦的不是类型错误，是**有人往请求里加金额**：
          「请求体不接受任何金额字段」是三条硬限制的第一条，一旦破了，
          建单时那道金额确认闸门当场失效。类型能被 as 绕过，这一行不能。
        */
        body: createPaymentRequestSchema.parse({ orderNo, channel }),
        schema: paymentInvokeSchema,
      })
    },
  }
}
